import React from "react";
import { buttonFeedbackStatus, type ButtonFeedback } from "../sequence/sequenceStateV4";

/**
 * icon หมุนท้ายชื่อการ์ด/หัวข้อ บอกว่าส่วนไหนกำลังโหลดหรือบันทึกอยู่
 *
 * ค่า `running` ต้องมาจากการ์ดที่ตรวจ `buttonLoadCheck` ของ sequence จริง
 * (BLC_STATUS.LOADING ของ action ของการ์ดนั้น) — ห้ามใส่ state ลอย ๆ เอง
 * เพราะถ้ามี text node หรือ icon ตอนที่ไม่กำลังโหลด จะทำให้ visual gate
 * (tools/compare.js เทียบกับ design bundle) เปลี่ยนไป
 */
export const SectionSpinner: React.FC<{ running: boolean; label: string }> = ({ running, label }) => {
  if (!running) return null;
  return <span className="hv4-spin" role="status" aria-live="polite" aria-label={label} title={label} />;
};

/** มี action ใดในรายการที่กำลัง LOADING หรือไม่ (busy = helper ของการ์ดที่ผูก card name ไว้แล้ว) */
export const anyBusy = (busy: (action: string) => boolean, actions: readonly string[]): boolean =>
  actions.some((action) => busy(action));

/**
 * สร้าง helper ของการ์ด 1 ใบ: fb(ACTIONS.SAVE_TRIAGE) -> "loading" | "done" | "error" | "idle"
 * ทุกปุ่มในหน้าคัดกรอง/ตรวจรักษาต้องเอาผลไปใส่ <ButtonV4 feedback={...}>
 * (ห้ามใส่ busy state ลอย ๆ เองใน card เพราะต้องตรงกับ state จริงของ sequence)
 */
export const makeActionFeedback = (
  buttonLoadCheck: Record<string, any> | undefined,
  sequence: string,
  card: string,
): ((action: string) => ButtonFeedback) => (action: string): ButtonFeedback =>
  buttonFeedbackStatus(buttonLoadCheck, { sequence, card, action });

/**
 * เครื่องหมายสถานะในปุ่ม: spinner ตอนกำลังทำ, ✓ เมื่อสำเร็จ, ! เมื่อพลาด
 * ไม่มีอะไรเลยตอน idle จึงไม่กระทบ visual compare (design bundle ไม่มีเครื่องหมายพวกนี้)
 */
export const FeedbackMark: React.FC<{ feedback: ButtonFeedback; label?: string }> = ({
  feedback,
  label,
}) => {
  if (feedback === "idle") return null;
  if (feedback === "loading")
    return <span className="hv4-btn-spin" aria-hidden="true" data-feedback="loading" title={label} />;
  if (feedback === "done")
    return <span className="hv4-btn-flag hv4-btn-flag-done" aria-hidden="true" data-feedback="done" title={label}>✓</span>;
  return <span className="hv4-btn-flag hv4-btn-flag-error" aria-hidden="true" data-feedback="error" title={label}>!</span>;
};
